/**
 * NoShowAgreementModal (Jose 2026-08-07) — the redemption gate for a no-show-blocked client.
 *
 * A client blocked after 2 back-to-back no-shows can't book normally (bookingService throws
 * NO_SHOW_BLOCK). This modal shows them the denial notice, the prepay terms, and an "I agree"
 * checkbox that acts as an electronic signature (with their typed name). On agree it returns a
 * `prepaidAgreement` record; the booking page re-calls createAppointment with it, which the
 * enforcement then lets through (booked as prepaid). The actual charge is taken in person / when
 * the payment backend is live — this captures the consent + commitment.
 */
import React, { useState } from 'react';

export interface PrepaidAgreement {
  agreedAt: number;      // epoch ms
  signerName: string;    // typed name = electronic signature
  method: 'prepaid-consent';
  notice: string;        // the exact notice they agreed to (audit trail)
}

interface Props {
  open: boolean;
  notice: string;
  clientName?: string;
  serviceLabel?: string;
  priceLabel?: string;
  submitting?: boolean;
  onClose: () => void;
  onAgree: (agreement: PrepaidAgreement) => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center',
  justifyContent: 'center', padding: 16, background: 'rgba(2,6,23,0.72)',
  fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
};
const card: React.CSSProperties = {
  width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto', background: '#fff',
  borderRadius: 22, boxShadow: '0 24px 60px rgba(0,0,0,.45)',
};

const NoShowAgreementModal: React.FC<Props> = ({
  open, notice, clientName, serviceLabel, priceLabel, submitting, onClose, onAgree,
}) => {
  const [agreed, setAgreed] = useState(false);
  const [name, setName] = useState(clientName || '');

  if (!open) return null;

  const canSubmit = agreed && name.trim().length >= 2 && !submitting;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: '#dc2626', color: '#fff', padding: '18px 24px', borderRadius: '22px 22px 0 0' }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.5, opacity: .9 }}>SLIK BARBER LOUNGE</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2 }}>🚫 Scheduling Restricted</div>
        </div>

        <div style={{ padding: '20px 24px 24px' }}>
          {/* The notice */}
          <p style={{ color: '#334155', fontSize: 15, lineHeight: 1.5, margin: '0 0 16px' }}>{notice}</p>

          {/* What they're booking */}
          {(serviceLabel || priceLabel) && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
              {serviceLabel && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <span style={{ color: '#64748b' }}>Service</span>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{serviceLabel}</span>
                </div>
              )}
              {priceLabel && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginTop: 6 }}>
                  <span style={{ color: '#64748b' }}>Paid upfront</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>{priceLabel}</span>
                </div>
              )}
            </div>
          )}

          {/* Signature name */}
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            Type your full name (electronic signature)
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '11px 12px', borderRadius: 10,
              border: '1px solid #cbd5e1', fontSize: 15, marginBottom: 14, fontFamily: 'inherit',
            }}
          />

          {/* Agreement checkbox = e-signature */}
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 18 }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }}
            />
            <span style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.45 }}>
              I agree to <strong>pay for my service in full before my appointment</strong>. I understand this
              agreement is required because of two no-shows, and that checking this box is my electronic signature.
            </span>
          </label>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              disabled={submitting}
              style={{
                flex: '0 0 auto', padding: '12px 16px', borderRadius: 12, border: '1px solid #e2e8f0',
                background: '#fff', color: '#475569', fontWeight: 600, fontSize: 15, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => canSubmit && onAgree({ agreedAt: Date.now(), signerName: name.trim(), method: 'prepaid-consent', notice })}
              disabled={!canSubmit}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 12, border: 'none',
                background: canSubmit ? '#16a34a' : '#cbd5e1', color: '#fff', fontWeight: 800, fontSize: 15,
                cursor: canSubmit ? 'pointer' : 'default',
              }}
            >
              {submitting ? 'Booking…' : 'Agree & Book (prepaid)'}
            </button>
          </div>
          <p style={{ color: '#94a3b8', fontSize: 11.5, textAlign: 'center', marginTop: 12 }}>
            Payment is collected at the shop. Complete 3 visits and this restriction is lifted automatically.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NoShowAgreementModal;
